/**
 * useAdminStatsSources — every source of the admin Statistics section, held at
 * app level so a tab switch loses nothing. docs/spec/01f-tab-loading.md,
 * "The app-level source hooks" → "useAdminStatsSources".
 *
 * The section (`AdminStatsSection`) is only a consumer: it requests the
 * one-shot sources (each with its own freshness window) and holds the two
 * listeners open while it is rendered. Before, all of it lived in
 * `AdminPanel`'s state and effects, so every open — and every return from
 * another tab, which unmounts the admin panel — read everything again, with
 * `0` on the tiles meanwhile.
 *
 * Costs, per open of the section with everything fresh: nothing but the two
 * re-subscriptions (counter shards ~10 docs, today + yesterday's climb logs).
 */
import { useCallback, useMemo, useRef } from "react";
import {
  collection, query, where, getDocs, getCountFromServer, getAggregateFromServer,
  onSnapshot, sum, Timestamp, doc, getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useDateKey } from "./useDateKey";
import { useRangeCache, useKeptListener } from "./useRangeCache";
import { countCurrentMAU } from "./useMAU";
import { loadOccupancy } from "./useOccupancy";
import { readPersistedPeriod } from "./usePersistedPeriod";
import { climbLogsToAscentShape, sprayLogsToAscentShape } from "../utils/climbLog";
import { comparePeriods, resolvePeriod } from "../utils/periodUtils";

const ONE_MINUTE_MS = 60 * 1000;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;

/** The distribution chart's stored period — the section and the prefetch read the same one. */
export const DIST_PERIOD_KEY = "period.adminAscentDist";
export const DIST_PERIOD_DEFAULT = Object.freeze({ type: "monthly" });
/** The comparison card's period on every open (deliberately not persisted). */
export const COMPARE_PERIOD_DEFAULT = Object.freeze({ type: "monthly", offset: 0 });

// The leavers' window: the 12 month buckets of the member-change chart.
const LEAVER_WINDOW_MONTHS = 12;

// The admin charts' rows: spray sends and repeats ride in the same day docs
// (E15), so they count as climbs, with no route difficulty and so no points.
export const adminClimbRows = (logs) => [...climbLogsToAscentShape(logs), ...sprayLogsToAscentShape(logs)];

/**
 * The one range both period charts are served from: the earliest `from` and
 * the latest `to` of the given windows, `null` (unbounded) winning on either
 * side. Windows that are missing (`prev` of an "all" comparison) are skipped.
 */
export function unionRange(windows) {
  let from;
  let to;
  for (const w of windows) {
    if (!w) continue;
    if (from === undefined) { from = w.from ?? null; to = w.to ?? null; continue; }
    from = from === null || w.from == null ? null : (w.from < from ? w.from : from);
    to = to === null || w.to == null ? null : (w.to > to ? w.to : to);
  }
  return { from: from ?? null, to: to ?? null };
}

/** The union range for a comparison period and a distribution period on `todayKey`. */
export function adminChartsRange(comparePeriod, distPeriod, todayKey) {
  const cmp = comparePeriods(comparePeriod, todayKey);
  const dist = resolvePeriod(distPeriod, todayKey);
  return unionRange([cmp.cur, cmp.prev, { from: dist.from, to: dist.to }]);
}

/** The gym-timezone day before `todayKey`. */
function dayBefore(todayKey) {
  if (!todayKey) return null;
  const d = new Date(todayKey + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/** Inclusive "YYYY-MM-DD" window on the `date` field — a single-field range, no composite index. */
async function readClimbLogs(gymId, from, to) {
  const constraints = [];
  if (from) constraints.push(where("date", ">=", from));
  if (to) constraints.push(where("date", "<=", to));
  const col = collection(db, "gyms", gymId, "climbLogs");
  const snap = await getDocs(constraints.length ? query(col, ...constraints) : col);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

const sumShards = (snap) => {
  let total = 0;
  snap.forEach(d => {
    const v = d.data()?.totalAscents;
    if (typeof v === "number") total += v;
  });
  return Math.max(0, total);
};

/**
 * @param {object} p
 * @param {string|null} p.gymId
 * @param {string|null} [p.timezone]  the gym's IANA zone — the day keys follow it
 */
export function useAdminStatsSources({ gymId, timezone = null }) {
  const todayKey = useDateKey(timezone);
  const yesterdayKey = useMemo(() => dayBefore(todayKey), [todayKey]);

  // ── adminStats doc ── The few numbers that need a history scan (30-day
  // traffic, rolling active-user sets) are pre-computed nightly by the
  // `updateAllGymAdminStats` Cloud Function into `private/adminStats`. One
  // getDoc, kept for an hour (the server writes it once a night; the section's
  // rebuild path forces a re-read). It RETURNS the doc — the rebuild check
  // awaits the data rather than reading the state.
  const [adminStats, adminStatsCache, adminStatsApi] = useRangeCache(gymId, {
    label: "fetchAdminStats",
    initial: null,
    freshMs: ONE_HOUR_MS,
    load: async () => {
      const snap = await getDoc(doc(db, `gyms/${gymId}/private/adminStats`));
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    },
  });
  const fetchAdminStats = useCallback((force = false) => adminStatsCache(null, null, force), [adminStatsCache]);

  // ── Climb-log rows of one range, for the comparison AND the distribution ──
  // Climb LOGS flattened to the ascent shape: an admin asking "what got climbed
  // this month" means every climb, not just the first sends. Each event carries
  // `kind`, so any consumer summing POINTS must skip repeats. `covers`: the
  // section requests the union of both charts' windows, and a narrower request
  // inside it is a hit — the default open reads the month once, not twice.
  const [climbLogRows, fetchClimbLogRows, climbLogRowsApi] = useRangeCache(gymId, {
    label: "fetchClimbLogRows",
    covers: true,
    freshMs: ONE_MINUTE_MS,
    load: async (from, to) => adminClimbRows(await readClimbLogs(gymId, from, to)),
  });

  // ── First sends: `count()` of the ascents collection ── There is no
  // server-side counter for them (the sharded `totalAscents` counts every
  // climb), and an aggregation bills per ~1000 index entries.
  const [firstSends, firstSendsCache, firstSendsApi] = useRangeCache(gymId, {
    label: "fetchFirstSends",
    initial: null,
    freshMs: 5 * ONE_MINUTE_MS,
    load: async () => {
      const snap = await getCountFromServer(collection(db, `gyms/${gymId}/ascents`));
      return Math.max(0, snap.data().count || 0);
    },
  });
  const fetchFirstSends = useCallback((force = false) => firstSendsCache(null, null, force), [firstSendsCache]);

  // ── MAU ── The month's count, kept 5 minutes. The recount on a real counter
  // change is triggered from the counter listener below.
  const [mau, mauCache, mauApi] = useRangeCache(gymId, {
    label: "fetchMau",
    initial: null,
    freshMs: 5 * ONE_MINUTE_MS,
    load: () => countCurrentMAU(gymId),
  });
  // The gym whose MAU a consumer asked for (the section, for a viewer who sees
  // the tile). Only that one is recounted on a counter change — the prefetch's
  // count is a warm-up, not a reason to pay for recounts nobody looks at.
  const mauWantedRef = useRef(null);
  const fetchMau = useCallback((force = false) => {
    mauWantedRef.current = gymId;
    return mauCache(null, null, force);
  }, [mauCache, gymId]);

  // ── Occupancy doc ── Computed once a day by the server.
  const [occupancy, occupancyCache, occupancyApi] = useRangeCache(gymId, {
    label: "fetchOccupancy",
    initial: null,
    freshMs: 6 * ONE_HOUR_MS,
    load: () => loadOccupancy(gymId),
  });
  const fetchOccupancy = useCallback((force = false) => occupancyCache(null, null, force), [occupancyCache]);

  // ── Leavers: `member.leave` / `member.kick` audit entries, 12 months ──
  // Single inequality on the timestamp → no composite index; the type filter
  // runs client-side because a gym's audit volume over a year is tiny.
  const [leaverDates, leaverCache, leaverApi] = useRangeCache(gymId, {
    label: "fetchLeaverDates",
    freshMs: ONE_HOUR_MS,
    load: async () => {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth() - (LEAVER_WINDOW_MONTHS - 1), 1);
      const snap = await getDocs(query(
        collection(db, "gyms", gymId, "auditLog"),
        where("timestamp", ">=", Timestamp.fromDate(start)),
      ));
      const out = [];
      snap.docs.forEach((d) => {
        const data = d.data();
        if (data.type !== "member.leave" && data.type !== "member.kick") return;
        const raw = data.timestamp;
        if (!raw) return;
        let dt;
        try { dt = raw.toDate ? raw.toDate() : new Date(raw); }
        catch { return; }
        if (isNaN(dt.getTime())) return;
        out.push(dt);
      });
      return out;
    },
  });
  const fetchLeaverDates = useCallback((force = false) => leaverCache(null, null, force), [leaverCache]);

  // ── Total-climbs seed: one `sum()` aggregation ── The real-time channel
  // queues behind the gym's initial sync on a cold page; the aggregation RPC
  // answers at once. Read only while the counter has delivered nothing.
  const [totalSeed, totalSeedCache, totalSeedApi] = useRangeCache(gymId, {
    label: "fetchTotalSeed",
    initial: null,
    freshMs: Infinity,
    load: async () => {
      const snap = await getAggregateFromServer(collection(db, `gyms/${gymId}/counters`), { total: sum("totalAscents") });
      return Math.max(0, snap.data().total || 0);
    },
  });

  // The MAU recount, reached from the counter listener's callback (which is
  // created at subscription time and must not hold a stale closure). Only a
  // count that has been asked for is recounted: a viewer without the MAU tile
  // never pays for one.
  const mauRecountRef = useRef(null);
  mauRecountRef.current = () => {
    if (mauWantedRef.current === gymId && mauApi.isLoaded()) mauCache(null, null, true);
  };

  // ── Total-climbs counter (sharded) ── Incremented atomically on every
  // climb; ~10 tiny docs. A REAL change while open forces one MAU recount:
  // only a later snapshot of the SAME subscription can be one — its first
  // snapshot (an open, or the kept value re-delivered on a return) is not.
  const [counters, countersApi] = useKeptListener(gymId, {
    label: "counters",
    clearOnError: true,
    subscribe: (onNext, onError) => {
      let last;
      return onSnapshot(
        collection(db, `gyms/${gymId}/counters`),
        (snap) => {
          const total = sumShards(snap);
          const changed = last !== undefined && total !== last;
          last = total;
          onNext(total);
          if (changed) mauRecountRef.current();
        },
        onError,
      );
    },
  });

  // ── Today + yesterday's climb logs (the live overlay) ── Yesterday matters
  // because the nightly `dailyCounts` is written at ~03:00: between midnight
  // and that run the doc lacks the just-ended day. One doc per climber per
  // day, so a couple of dozen docs even in a busy gym. Keyed by
  // `yesterdayKey`: a new day drops the kept overlay before it re-subscribes.
  // Its error is logged, not swallowed — this one listener makes today live
  // on three charts, and they fall back to the nightly numbers without it.
  const [recentLogs, recentApi] = useKeptListener(gymId, {
    label: "recent climbLogs",
    key: yesterdayKey,
    clearOnError: true,
    subscribe: (onNext, onError) => onSnapshot(
      query(collection(db, `gyms/${gymId}/climbLogs`), where("date", ">=", yesterdayKey)),
      (snap) => onNext(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
      onError,
    ),
  });

  const countersOpen = countersApi.open;
  const recentOpen = recentApi.open;
  const countersLoaded = countersApi.isLoaded;

  /**
   * Hold the two listeners while the section is rendered; returns the release.
   * While the counter has delivered nothing on this gym, the `sum()` seed is
   * read once (its `freshMs` is unbounded, so a second open never re-reads it).
   */
  const open = useCallback(() => {
    const releaseCounters = countersOpen();
    const releaseRecent = recentOpen();
    if (!countersLoaded()) totalSeedCache();
    return () => { releaseCounters(); releaseRecent(); };
  }, [countersOpen, recentOpen, countersLoaded, totalSeedCache]);

  // The prefetch reads the stored period the distribution chart will open on.
  const todayKeyRef = useRef(todayKey);
  todayKeyRef.current = todayKey;

  /**
   * The one-shot reads only, with the default periods — never a listener (a
   * listener would pay for every change while nobody looks). Each read keeps
   * its own freshness window, so a prefetch right after an open reads nothing.
   */
  // `advanced` / `withMau`: the same rules the section applies, so a prefetch
  // never pays for a read the section would not make (the detailed charts'
  // sources need advancedStats; MAU is a billing figure).
  const prefetch = useCallback(({ advanced = true, withMau = true } = {}) => {
    if (!gymId) return Promise.resolve();
    const reads = [fetchFirstSends()];
    // not `fetchMau`: a prefetched count is not a wanted one (no recounts)
    if (withMau) reads.push(mauCache());
    if (advanced) {
      const day = todayKeyRef.current;
      const distPeriod = readPersistedPeriod(DIST_PERIOD_KEY, DIST_PERIOD_DEFAULT, gymId);
      const range = adminChartsRange(COMPARE_PERIOD_DEFAULT, distPeriod, day);
      reads.push(fetchAdminStats(), fetchClimbLogRows(range.from, range.to), fetchOccupancy(), fetchLeaverDates());
    }
    return Promise.all(reads).then(() => undefined);
  }, [gymId, fetchAdminStats, fetchClimbLogRows, fetchFirstSends, mauCache, fetchOccupancy, fetchLeaverDates]);

  const status = useMemo(() => ({
    adminStats: adminStatsApi.status,
    climbLogRows: climbLogRowsApi.status,
    counters: countersApi.status,
    totalSeed: totalSeedApi.status,
    firstSends: firstSendsApi.status,
    mau: mauApi.status,
    occupancy: occupancyApi.status,
    recentLogs: recentApi.status,
    leaverDates: leaverApi.status,
  }), [
    adminStatsApi.status, climbLogRowsApi.status, countersApi.status, totalSeedApi.status,
    firstSendsApi.status, mauApi.status, occupancyApi.status, recentApi.status, leaverApi.status,
  ]);

  return useMemo(() => ({
    gymId, todayKey, yesterdayKey, status,
    adminStats, fetchAdminStats,
    climbLogRows, fetchClimbLogRows,
    counters, totalSeed,
    firstSends, fetchFirstSends,
    mau, fetchMau,
    occupancy, fetchOccupancy,
    recentLogs,
    leaverDates, fetchLeaverDates,
    open, prefetch,
  }), [
    gymId, todayKey, yesterdayKey, status,
    adminStats, fetchAdminStats, climbLogRows, fetchClimbLogRows, counters, totalSeed,
    firstSends, fetchFirstSends, mau, fetchMau, occupancy, fetchOccupancy, recentLogs,
    leaverDates, fetchLeaverDates, open, prefetch,
  ]);
}
